import {
	asApiError,
	AutoCompleteResultFilter,
	DayOfWeek,
	IDailyIntervals,
	IInterval,
	IMeetingCategory,
	IOperationResultNoValue,
	IUser,
	IUserReference,
	MeetingConfigViewModel,
	ResourceAutoCompleteViewModelType,
} from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useDebounceCallback } from '../../../../../../hooks/useDebounceValue';
import { useErrorMessages, useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../../models/Logging';
import { getDisplayName } from '../../../../../../models/NameUtils';
import { useMeetingCategoriesQuery } from '../../../../../../queries/Scheduler/useMeetingCategoriesQuery';
import { useSchedulerConfigUpdateCategoryMutation } from '../../../../../../queries/Scheduler/useUpdateMeetingConfigCategoryMutation';
import { useSchedulerConfigUpdateMaxPerDayMutation } from '../../../../../../queries/Scheduler/useUpdateMeetingConfigMaxPerDayMutation';
import { useSchedulerConfigUpdateMaxPerMonthMutation } from '../../../../../../queries/Scheduler/useUpdateMeetingConfigMaxPerMonthMutation';
import {
	ISimpleAutoCompleteSearchFieldItemSelectionEvent,
	SimpleAutoCompleteSearchField,
} from '../../../../autocomplete/SimpleAutoCompleteSearchField';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../../../components-v2/Select';
import { Button } from '../../../../../components-v2/Button';
import { MeetingCategoriesDialog } from '../../MeetingCategories/MeetingCategoriesDialog';
import { buildCategoryLimitHint } from '../../MeetingCategories/meetingCategoryUtils';
import { XIcon } from '../../../../svgs/icons/XIcon';
import { Input } from '../../../../../components-v2/Input';
import { Toggle } from '../../../../Toggle';
import { AvailabilityTimeProperty, MeetingConfigAvailability } from '../../MeetingConfigAvailability';
import { styleSheet } from '../styles';

interface IProps {
	meeting: MeetingConfigViewModel;
}

const defaultIncrement = 30;

const noCategoryValue = 'none';

const yesNoOptions: { label: string; value: string; allowSameDay: boolean }[] = [
	{ label: 'No', value: 'no', allowSameDay: false },
	{ label: 'Yes', value: 'yes', allowSameDay: true },
];

const hoursOrDaysOptions: { label: string; value: 'days' | 'hours' }[] = [
	{ label: 'days', value: 'days' },
	{ label: 'hours', value: 'hours' },
];

const timeInAdvanceOptions = Array.from({ length: 30 }, (_, i) => ({ label: `${i}`, value: `${i}` }));

const defaultInterval = {
	endMinutes: 1020,
	startMinutes: 540,
};

const defaultAvailability: IDailyIntervals[] = [
	null,
	{ intervals: [defaultInterval] },
	{ intervals: [defaultInterval] },
	{ intervals: [defaultInterval] },
	{ intervals: [defaultInterval, defaultInterval] },
	{ intervals: [defaultInterval] },

	null,
];

const lookForHoursOrDaysRegex = new RegExp(/([0-9]|[1-9][0-9])\./);

export const SchedulingLimits: React.FC<IProps> = observer(({ meeting }) => {
	const logger = useEventLogging();
	const errorMessages = useErrorMessages();
	const userSession = useUserSession();

	// Create a filter to exclude the current user from the search results
	const userFilter = useMemo(() => {
		const currentUserId = userSession?.user?.id;

		// Function that checks if a user is not the current user
		const filterFn: AutoCompleteResultFilter<IUser> = (user, _query) => {
			if (!currentUserId) return true; // If no current user, include all results
			return user.id !== currentUserId; // Only include users that aren't the current user
		};

		return filterFn;
	}, [userSession?.user?.id]);

	const [meetingAvailability, setMeetingAvailability] = useState(meeting?.availability ?? defaultAvailability);
	const [allowSameDayMeetings, setAllowSameDayMeetings] = useState<(typeof yesNoOptions)[0]>(
		meeting?.allowSameDay ? yesNoOptions[1] : yesNoOptions[0]
	);

	const matchIfHaveDays = meeting?.atLeastInterval.match(lookForHoursOrDaysRegex);

	const [hoursOrDays, setHoursOrDays] = useState(
		hoursOrDaysOptions.find(i => {
			if (matchIfHaveDays) {
				return i.value === 'days';
			}
			return i.value === 'hours';
		}) ?? hoursOrDaysOptions[0]
	);

	const [meetingAtLeastInterval, setMeetingAtLeastInterval] = useState(
		timeInAdvanceOptions.find(i => {
			if (matchIfHaveDays) {
				return i.value === String(Number(matchIfHaveDays[0].replace('.', '')));
			}

			return i.value === String(parseInt(meeting?.atLeastInterval.split(':')[0], 10));
		}) ?? timeInAdvanceOptions[0]
	);

	const [maxPerDay, setMaxPerDay] = useState(meeting?.maxPerDay || 0);
	const [maxPerMonth, setMaxPerMonth] = useState(meeting?.maxPerMonth || 0);
	const [additionalHosts, setAdditionalHosts] = useState<IUserReference[]>(meeting?.additionalHosts || []);
	const [isCategoriesDialogOpen, setIsCategoriesDialogOpen] = useState(false);

	const categoriesQuery = useMeetingCategoriesQuery({ userId: meeting?.forUser?.id });
	const loadedCategories = categoriesQuery.data?.values ?? [];
	const assignedCategory = loadedCategories.find(category => category.id === meeting?.categoryId);
	// A settled list is the authority on what still exists, so a category deleted from the dialog stops showing
	// as selected even though the config held here keeps the stale id until it is refetched. While the list is
	// absent or refetching, the category hydrated on the config stands in, which covers the first load and the
	// window after creating a category here, where it is already assigned but not yet in the list.
	const categoryListIsSettled = categoriesQuery.isSuccess && !categoriesQuery.isFetching;
	const selectedCategory = assignedCategory ?? (categoryListIsSettled ? undefined : meeting?.category);
	const categories =
		selectedCategory && !loadedCategories.some(category => category.id === selectedCategory.id)
			? [selectedCategory, ...loadedCategories]
			: loadedCategories;
	// Only once the list has loaded, so the empty state cannot flash before the categories arrive.
	const hasNoCategories = !categoriesQuery.isLoading && categories.length === 0;

	useEffect(() => {
		meeting.setAsEditing()?.catch((err: IOperationResultNoValue) => {
			logger.logApiError('MeetingConfigSetAsEditing-Error', err);
			// do not need to show user that this failed...
		});
		meeting.getAutomationChoices();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onAllowSameDayMeetingsChange = (option: (typeof yesNoOptions)[0]) => {
		setAllowSameDayMeetings(option);
		meeting.allowSameDay = option.allowSameDay;
	};

	const onChangeHoursOrDays = (option: (typeof hoursOrDaysOptions)[0]) => {
		setHoursOrDays(option);
		setMeetingAtLeastInterval(timeInAdvanceOptions[0]);
		const hours = option.value === 'hours' ? 1 : 0;
		const days = option.value === 'days' ? 1 : 0;
		meeting.setAtLeastInterval(hours, days);
	};

	const onChangeMeetingAtLeastInterval = (option: (typeof timeInAdvanceOptions)[0]) => {
		setMeetingAtLeastInterval(option);
		const num = Number(option.value);
		const hours = hoursOrDays.value === 'hours' ? num : 0;
		const days = hoursOrDays.value === 'days' ? num : 0;
		meeting.setAtLeastInterval(hours, days);
	};

	const maxPerDayMutation = useSchedulerConfigUpdateMaxPerDayMutation({
		onSuccess: data => meeting.setConfig(data),
		onError: () => {
			setMaxPerDay(meeting.maxPerDay || 0);
			toast.error('Failed to update maximum meetings per day');
		},
		scope: { id: `meeting-config-maxPerDay-${meeting?.id}` },
	});

	const maxPerMonthMutation = useSchedulerConfigUpdateMaxPerMonthMutation({
		onSuccess: data => meeting.setConfig(data),
		onError: () => {
			setMaxPerMonth(meeting.maxPerMonth || 0);
			toast.error('Failed to update maximum meetings per month');
		},
		scope: { id: `meeting-config-maxPerMonth-${meeting?.id}` },
	});

	const categoryMutation = useSchedulerConfigUpdateCategoryMutation({
		onSuccess: data => meeting.setConfig(data),
		onError: () => toast.error('Failed to update the meeting category'),
		scope: { id: `meeting-config-category-${meeting?.id}` },
	});

	const onCategoryChange = (value: string) => {
		categoryMutation.mutate({
			configId: meeting.id,
			categoryId: value === noCategoryValue ? undefined : value,
			forUserId: meeting.forUser?.id,
		});
	};

	// Reaching Manage categories from an unset picker means the category being created is the one wanted
	// here, so it gets assigned. An already assigned category is never replaced.
	const onCategoryCreated = (created: IMeetingCategory) => {
		if (!created?.id || selectedCategory || categoryMutation.isPending) {
			return;
		}
		categoryMutation.mutate({ configId: meeting.id, categoryId: created.id, forUserId: meeting.forUser?.id });
	};

	const debouncedSaveMaxPerDay = useDebounceCallback((num: number) => {
		maxPerDayMutation.mutate({ configId: meeting.id, maxPerDay: num, forUserId: meeting.forUser?.id });
	}, 500);

	const debouncedSaveMaxPerMonth = useDebounceCallback((num: number) => {
		maxPerMonthMutation.mutate({ configId: meeting.id, maxPerMonth: num, forUserId: meeting.forUser?.id });
	}, 500);

	const onMaxPerDayChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.value === '') {
			setMaxPerDay(undefined);
			debouncedSaveMaxPerDay(0);
			return;
		}
		const num = parseInt(e.target.value, 10);
		if (isNaN(num)) {
			setMaxPerDay(undefined);
			debouncedSaveMaxPerDay(0);
			return;
		}
		setMaxPerDay(num);
		debouncedSaveMaxPerDay(num);
	};

	const onMaxPerDayToggled = () => {
		debouncedSaveMaxPerDay.cancel();
		if (maxPerDay > 0) {
			setMaxPerDay(0);
			maxPerDayMutation.mutate({ configId: meeting.id, maxPerDay: 0, forUserId: meeting.forUser?.id });
			return;
		}
		setMaxPerDay(3);
		maxPerDayMutation.mutate({ configId: meeting.id, maxPerDay: 3, forUserId: meeting.forUser?.id });
	};

	const onMaxPerMonthChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.value === '') {
			setMaxPerMonth(undefined);
			debouncedSaveMaxPerMonth(0);
			return;
		}
		const num = parseInt(e.target.value, 10);
		if (isNaN(num)) {
			setMaxPerMonth(undefined);
			debouncedSaveMaxPerMonth(0);
			return;
		}
		setMaxPerMonth(num);
		debouncedSaveMaxPerMonth(num);
	};

	const onMaxPerMonthToggled = () => {
		debouncedSaveMaxPerMonth.cancel();
		if (maxPerMonth > 0) {
			setMaxPerMonth(0);
			maxPerMonthMutation.mutate({ configId: meeting.id, maxPerMonth: 0, forUserId: meeting.forUser?.id });
			return;
		}
		setMaxPerMonth(30);
		maxPerMonthMutation.mutate({ configId: meeting.id, maxPerMonth: 30, forUserId: meeting.forUser?.id });
	};

	const onTimeChange = (day: DayOfWeek, block: number, property: AvailabilityTimeProperty, value: number) => {
		const updated: any = { ...meetingAvailability[day] };
		if (updated?.intervals?.length) {
			updated.intervals[block][property] = value;

			// ensure that endMinutes are never earlier than startminutes
			if (property === 'startMinutes' && updated.intervals[block].endMinutes <= updated.intervals[block].startMinutes) {
				updated.intervals[block].endMinutes = updated.intervals[block].startMinutes + defaultIncrement;
			}

			meeting
				.setAvailability(day, updated)
				?.then(() => {
					setMeetingAvailability(meeting.availability);
				})
				?.catch((err: IOperationResultNoValue) => {
					logger.logApiError('ScheduleMeetingAvailabilityTimeChange-Error', err);

					errorMessages.pushApiError(err);
				});
			return;
		}

		toast.error('Error getting availabilities.');
		logger.logApiError(
			'MeetingConfigModal-onTimeChange-Error',
			asApiError(new Error('updated.intervals does not exist'))
		);
	};

	const addDay = (day: DayOfWeek) => {
		const update = { intervals: [defaultInterval] };
		updateAvailability(day, update);
	};

	const addSubblock = (day: DayOfWeek) => {
		const intervals = meetingAvailability[day]?.intervals;
		if (!intervals?.length) {
			return;
		}

		const lastInterval = intervals[intervals.length - 1];
		const newInterval: IInterval = {
			endMinutes: lastInterval.endMinutes + defaultIncrement * 2,
			startMinutes: lastInterval.endMinutes + defaultIncrement,
		};
		const update = {
			intervals: [...intervals, newInterval],
		};
		updateAvailability(day, update);
	};

	const removeSubblock = (day: DayOfWeek) => {
		const newIntervals = meetingAvailability[day].intervals;

		newIntervals.pop();
		const update = {
			intervals: newIntervals,
		};
		updateAvailability(day, update);
	};

	const updateAvailability = (day: DayOfWeek, update: IDailyIntervals) => {
		meeting
			.setAvailability(day, update)
			?.then(() => {
				setMeetingAvailability(
					meetingAvailability.map((a, i) => {
						if (i === day) {
							a = update;
						}

						return a;
					})
				);
			})
			?.catch((err: IOperationResultNoValue) => {
				logger.logApiError('ScheduleMeetingAvailabilityAddDay-Error', err);

				errorMessages.pushApiError(err);
			});
	};

	const toggleAvailableDay = (day: number) => {
		const updated = meetingAvailability.map((a, idx) => {
			return meetingAvailability[day]
				? idx === day
					? null
					: a
				: idx === day
					? {
							intervals: [
								{
									endMinutes: 1020,
									startMinutes: 540,
								},
							],
						}
					: a;
		});

		meeting

			.setAvailability(day, updated[day])
			?.then(() => {
				setMeetingAvailability(updated);
			})
			?.catch((err: IOperationResultNoValue) => {
				logger.logApiError('ScheduleMeetingAvailability-Error', err);

				errorMessages.pushApiError(err);
			});
	};

	const onUserSelected = (e: ISimpleAutoCompleteSearchFieldItemSelectionEvent<IUser>) => {
		const user = e.selection;
		if (user && !additionalHosts.some(host => host.id === user.id) && additionalHosts.length < 3) {
			const userReference: IUserReference = {
				id: user.id,
				firstName: user.firstName,
				lastName: user.lastName,
				email: user.email,
			};

			const newHosts = [...additionalHosts, userReference];
			setAdditionalHosts(newHosts);
			meeting.setAdditionalHosts(newHosts);
			if (e.target) {
				e.target.clearInput();
			}
		}
	};

	const removeAdditionalHost = (userId: string) => {
		const newHosts = additionalHosts.filter(host => host.id !== userId);
		setAdditionalHosts(newHosts);
		meeting.setAdditionalHosts(newHosts);
	};

	const isHoursInAdvance = hoursOrDays.value === 'hours';

	const categoryLimitHint = buildCategoryLimitHint(selectedCategory);

	return (
		<div className={css(styleSheet.wrapper)}>
			<div className={css(styleSheet.col)}>
				<h3 className={css(styleSheet.sectionTitle)}>Scheduling Limits</h3>
				<p className={css(styleSheet.label)}>Scheduling Limits</p>
				<div className={css(styleSheet.subLabel)}>
					<b>Allow invitees to schedule immediately...</b>
				</div>
				<Select
					value={allowSameDayMeetings.value}
					onValueChange={value => {
						const option = yesNoOptions.find(o => o.value === value);
						if (option) {
							onAllowSameDayMeetingsChange(option);
						}
					}}
				>
					<SelectTrigger styles={styleSheet.smallInput}>
						<span>{allowSameDayMeetings.label}</span>
					</SelectTrigger>
					<SelectContent>
						{yesNoOptions.map(option => (
							<SelectItem key={option.value} value={option.value}>
								{option.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				{!allowSameDayMeetings.allowSameDay && (
					<div className={css(styleSheet.numberInputContainer)}>
						<span className={css(styleSheet.text)}>Invitees must schedule meetings&nbsp;&nbsp;</span>
						<Select
							value={meetingAtLeastInterval.value}
							onValueChange={value => {
								const option = timeInAdvanceOptions.find(o => o.value === value);
								if (option) {
									onChangeMeetingAtLeastInterval(option);
								}
							}}
						>
							<SelectTrigger styles={[styleSheet.smallInput, styleSheet.numberInput, styleSheet.timeInAdvanceSelect]}>
								<span>{meetingAtLeastInterval.label}</span>
							</SelectTrigger>
							<SelectContent>
								{[...timeInAdvanceOptions].slice(0, isHoursInAdvance ? 23 : 30).map(option => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						&nbsp;
						<Select
							value={hoursOrDays.value}
							onValueChange={value => {
								const option = hoursOrDaysOptions.find(o => o.value === value);
								if (option) {
									onChangeHoursOrDays(option);
								}
							}}
						>
							<SelectTrigger styles={styleSheet.hoursOrDaysSelect}>
								<span>{hoursOrDays.label}</span>
							</SelectTrigger>
							<SelectContent>
								{hoursOrDaysOptions.map(option => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						&nbsp;
						<span className={css(styleSheet.text)}>in advance</span>
					</div>
				)}
				<div className={css(styleSheet.spacer)} />
				<p className={css(styleSheet.label)}>Include others on this meeting</p>
				<div className={css(styleSheet.subLabel)}>
					The calendar will only show times when all participants are available
				</div>
				<div className={css(styleSheet.additionalHostsContainer)}>
					<SimpleAutoCompleteSearchField
						placeholder='Search employees...'
						type={ResourceAutoCompleteViewModelType.User}
						// @ts-ignore - The type definition might be slightly off
						onItemSelected={onUserSelected}
						resultsLimit={5}
						pageSize={5}
						className={css(styleSheet.additionalHostsInput)}
						disabled={additionalHosts.length >= 3}
						autoCompleteFilter={userFilter}
					/>
					{additionalHosts.length > 0 && (
						<div className={css(styleSheet.additionalHostsList)}>
							{additionalHosts.map(host => (
								<div key={host.id} className={css(styleSheet.additionalHostItem)}>
									<div className={css(styleSheet.hostDetails)}>
										<span className={css(styleSheet.additionalHostName)}>{getDisplayName(host)}</span>
										<span className={css(styleSheet.additionalHostEmail)}>{host.email}</span>
									</div>
									<button
										className={css(styleSheet.removeHostButton)}
										onClick={() => removeAdditionalHost(host.id)}
										aria-label={`Remove ${getDisplayName(host)}`}
									>
										<XIcon height={12} width={12} />
									</button>
								</div>
							))}
						</div>
					)}
				</div>
				<div className={css(styleSheet.spacer)} />
				<p className={css(styleSheet.label)}>Meeting Category</p>
				<div className={css(styleSheet.subLabel)}>
					Meetings in the same category share one set of daily/monthly maximums, so several links can draw on one quota.
				</div>
				<div className={css(styleSheet.categoryRow)}>
					{hasNoCategories ? (
						<Button variant='secondary' size='sm' onClick={() => setIsCategoriesDialogOpen(true)}>
							Add meeting category
						</Button>
					) : (
						<>
							<Select value={selectedCategory?.id ?? noCategoryValue} onValueChange={onCategoryChange}>
								<SelectTrigger styles={styleSheet.categorySelect}>
									<span>{selectedCategory?.name ?? 'No category'}</span>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={noCategoryValue}>No category</SelectItem>
									{categories.map(category => (
										<SelectItem key={category.id} value={category.id}>
											{category.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<Button variant='link' size='sm' onClick={() => setIsCategoriesDialogOpen(true)}>
								Manage categories
							</Button>
						</>
					)}
				</div>
				<div className={css(styleSheet.spacer)} />
				<p className={css(styleSheet.label)}>Max Meeting Limits</p>
				<div className={css(styleSheet.subLabel)}>
					<b>Setup the max amount of meetings of this type allowed</b>
				</div>
				<div className={css(styleSheet.numberInputContainer)}>
					<Toggle
						id='max-same-day-meetings-toggle'
						isOn={maxPerDay > 0}
						onToggleCheckChanged={onMaxPerDayToggled}
						uncheckedColor='#ccc'
						className={css(styleSheet.toggle)}
					/>
					<Input
						styles={[styleSheet.smallInput, styleSheet.numberInput]}
						id='max-same-day-meetings-input'
						autoComplete='off'
						min={1}
						max={10}
						onChange={onMaxPerDayChanged}
						step={1}
						type='number'
						value={maxPerDay}
					/>
					<span className={css(styleSheet.text, maxPerDay === 0 && styleSheet.textDisabled)}>
						Maximum meetings per <u>day</u>
					</span>
				</div>
				<div className={css(styleSheet.numberInputContainer)}>
					<Toggle
						id='max-same-month-meetings-toggle'
						isOn={maxPerMonth > 0}
						onToggleCheckChanged={onMaxPerMonthToggled}
						uncheckedColor='#ccc'
						className={css(styleSheet.toggle)}
					/>
					<Input
						styles={[styleSheet.smallInput, styleSheet.numberInput]}
						id='max-same-month-meetings-input'
						autoComplete='off'
						min={1}
						max={50}
						onChange={onMaxPerMonthChanged}
						step={1}
						type='number'
						value={maxPerMonth}
						disabled={maxPerMonth === 0}
					/>
					<span className={css(styleSheet.text, maxPerMonth === 0 && styleSheet.textDisabled)}>
						Maximum meetings per <u>month</u>
					</span>
				</div>
				{categoryLimitHint ? <div className={css(styleSheet.categoryLimitHint)}>{categoryLimitHint}</div> : null}
				<div className={css(styleSheet.spacer)} />
			</div>
			<div className={css(styleSheet.col)}>
				<div className={css(styleSheet.availabilitiesContainer)}>
					<MeetingConfigAvailability
						meetingAvailability={meetingAvailability}
						onAddBlock={addSubblock}
						onAddDay={addDay}
						onChangeTime={onTimeChange}
						onRemoveBlock={removeSubblock}
						onToggleAvailable={toggleAvailableDay}
						increment={defaultIncrement}
					/>
				</div>
			</div>

			<MeetingCategoriesDialog
				isOpen={isCategoriesDialogOpen}
				onOpenChange={setIsCategoriesDialogOpen}
				forUserId={meeting?.forUser?.id}
				onCategoryCreated={onCategoryCreated}
			/>
		</div>
	);
});
